import { arrayMove } from '@dnd-kit/sortable'
import {
  TaskTrackerBoardCard,
  TaskTrackerBoardColumn,
  MoveTaskTrackerBoardItemType,
} from '../store/taskTracker.types'
import { mergeTaskStickerValues } from './taskTrackerTaskStickers.utils'

export const TASK_CARD_DND_PREFIX = 'card:'
export const TASK_COLUMN_DND_PREFIX = 'column:'
export const TASK_COLUMN_SORT_DND_PREFIX = 'column-sort:'
export const TASK_LANE_SORT_DND_PREFIX = 'lane-sort:'
export const TASK_TRACKER_NO_DND_ATTR = 'data-task-tracker-no-dnd'

export type TaskTrackerCardDragPlacement = {
  activeCardId: number
  sourceColumnId: number
  sourceIndex: number
  targetColumnId: number
  targetIndex: number
  placeholderHeight: number
}

export const stopKanbanCardDragEvent = (event: {
  stopPropagation: () => void
}) => {
  event.stopPropagation()
}

export const taskTrackerNoDndProps = {
  [TASK_TRACKER_NO_DND_ATTR]: true,
  onPointerDown: stopKanbanCardDragEvent,
  onMouseDown: stopKanbanCardDragEvent,
  onTouchStart: stopKanbanCardDragEvent,
} as const

export const isTaskTrackerNoDndEventTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return false

  return Boolean(target.closest(`[${TASK_TRACKER_NO_DND_ATTR}]`))
}

export const toTaskCardDndId = (cardId: number) =>
  `${TASK_CARD_DND_PREFIX}${cardId}`

export const toTaskColumnDndId = (columnId: number) =>
  `${TASK_COLUMN_DND_PREFIX}${columnId}`

export const toTaskColumnSortDndId = (columnId: number) =>
  toTaskLaneSortDndId('column', columnId)

export const toTaskLaneSortDndId = (
  type: MoveTaskTrackerBoardItemType,
  itemId: number,
) => `${TASK_LANE_SORT_DND_PREFIX}${type}:${itemId}`

export const parseTaskCardDndId = (id: string | number): number | null => {
  const value = String(id)
  if (!value.startsWith(TASK_CARD_DND_PREFIX)) return null

  const parsed = Number(value.slice(TASK_CARD_DND_PREFIX.length))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const parseTaskColumnDndId = (id: string | number): number | null => {
  const value = String(id)
  if (!value.startsWith(TASK_COLUMN_DND_PREFIX)) return null

  const parsed = Number(value.slice(TASK_COLUMN_DND_PREFIX.length))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const parseTaskLaneSortDndId = (
  id: string | number,
): { type: MoveTaskTrackerBoardItemType; itemId: number } | null => {
  const value = String(id)
  if (!value.startsWith(TASK_LANE_SORT_DND_PREFIX)) return null

  const payload = value.slice(TASK_LANE_SORT_DND_PREFIX.length)
  const separatorIndex = payload.indexOf(':')
  if (separatorIndex <= 0) return null

  const type = payload.slice(0, separatorIndex)
  if (type !== 'column' && type !== 'summary') return null

  const parsed = Number(payload.slice(separatorIndex + 1))
  if (!Number.isFinite(parsed) || parsed <= 0) return null

  return { type, itemId: parsed }
}

export const parseTaskColumnSortDndId = (
  id: string | number,
): number | null => {
  const lane = parseTaskLaneSortDndId(id)
  if (lane?.type === 'column') return lane.itemId

  const value = String(id)
  if (!value.startsWith(TASK_COLUMN_SORT_DND_PREFIX)) return null

  const parsed = Number(value.slice(TASK_COLUMN_SORT_DND_PREFIX.length))
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const findColumnIdByCardId = (
  columns: TaskTrackerBoardColumn[],
  cardId: number,
): number | null => {
  for (const column of columns) {
    if (column.cards.some((card) => card.id === cardId)) {
      return column.id
    }
  }

  return null
}

export const getCardPlacement = (
  columns: TaskTrackerBoardColumn[],
  cardId: number,
): { columnId: number; index: number } | null => {
  const columnId = findColumnIdByCardId(columns, cardId)
  if (columnId == null) return null

  const column = columns.find((item) => item.id === columnId)
  if (!column) return null

  const index = column.cards.findIndex((card) => card.id === cardId)
  if (index === -1) return null

  return { columnId, index }
}

const resolveOverColumnId = (
  columns: TaskTrackerBoardColumn[],
  overDndId: string,
): number | null => {
  const columnId = parseTaskColumnDndId(overDndId)
  if (columnId != null) return columnId

  const cardId = parseTaskCardDndId(overDndId)
  if (cardId == null) return null

  return findColumnIdByCardId(columns, cardId)
}

export const getCardDropPlacement = (
  columns: TaskTrackerBoardColumn[],
  activeCardId: number,
  overDndId: string,
  activeTop: number | undefined,
  overRect: { top: number; height: number } | null | undefined,
): Omit<TaskTrackerCardDragPlacement, 'placeholderHeight'> | null => {
  const source = getCardPlacement(columns, activeCardId)
  const targetColumnId = resolveOverColumnId(columns, overDndId)
  if (!source || targetColumnId == null) return null

  const targetColumn = columns.find((column) => column.id === targetColumnId)
  if (!targetColumn) return null

  const overCardId = parseTaskCardDndId(overDndId)
  const isOverColumnRoot = parseTaskColumnDndId(overDndId) != null
  const targetCardsCount =
    targetColumn.cards.length - (source.columnId === targetColumnId ? 1 : 0)
  let targetIndex = targetCardsCount

  if (overCardId != null && overCardId !== activeCardId) {
    const overIndex = targetColumn.cards.findIndex(
      (card) => card.id === overCardId,
    )
    if (overIndex === -1) return null

    if (source.columnId === targetColumnId) {
      targetIndex = overIndex
    } else {
      const isBelowOverItem =
        activeTop != null &&
        overRect != null &&
        activeTop > overRect.top + overRect.height / 2

      targetIndex = overIndex + (isBelowOverItem ? 1 : 0)
    }
  } else if (!isOverColumnRoot && overCardId !== activeCardId) {
    return null
  } else if (overCardId === activeCardId) {
    targetIndex = source.index
  }

  return {
    activeCardId,
    sourceColumnId: source.columnId,
    sourceIndex: source.index,
    targetColumnId,
    targetIndex: Math.max(0, Math.min(targetIndex, targetCardsCount)),
  }
}

export const moveCardToPlacement = (
  columns: TaskTrackerBoardColumn[],
  placement: Omit<TaskTrackerCardDragPlacement, 'placeholderHeight'>,
): TaskTrackerBoardColumn[] | null => {
  const sourceColumn = columns.find(
    (column) => column.id === placement.sourceColumnId,
  )
  const targetColumn = columns.find(
    (column) => column.id === placement.targetColumnId,
  )
  const card = sourceColumn?.cards.find(
    (item) => item.id === placement.activeCardId,
  )
  if (!sourceColumn || !targetColumn || !card) return null

  const sourceCards = sourceColumn.cards.filter(
    (item) => item.id !== placement.activeCardId,
  )
  const targetCards =
    sourceColumn.id === targetColumn.id
      ? sourceCards
      : targetColumn.cards.filter((item) => item.id !== placement.activeCardId)
  const targetIndex = Math.max(
    0,
    Math.min(placement.targetIndex, targetCards.length),
  )

  if (
    sourceColumn.id === targetColumn.id &&
    sourceColumn.cards[targetIndex]?.id === placement.activeCardId
  ) {
    return columns
  }

  const nextTargetCards = [...targetCards]
  nextTargetCards.splice(targetIndex, 0, card)

  return columns.map((column) => {
    if (column.id === targetColumn.id) {
      return {
        ...column,
        cards: nextTargetCards,
        cardsCount: nextTargetCards.length,
      }
    }

    if (column.id === sourceColumn.id) {
      return {
        ...column,
        cards: sourceCards,
        cardsCount: sourceCards.length,
      }
    }

    return column
  })
}

export const moveCardInColumns = (
  columns: TaskTrackerBoardColumn[],
  activeCardId: number,
  overDndId: string,
  activeTop: number | undefined,
  overRect: { top: number; height: number } | null | undefined,
): TaskTrackerBoardColumn[] | null => {
  const activeColumnId = findColumnIdByCardId(columns, activeCardId)
  const overColumnId = resolveOverColumnId(columns, overDndId)

  if (activeColumnId == null || overColumnId == null) return null

  const activeColumn = columns.find((column) => column.id === activeColumnId)
  const overColumn = columns.find((column) => column.id === overColumnId)

  if (!activeColumn || !overColumn) return null

  const activeIndex = activeColumn.cards.findIndex(
    (card) => card.id === activeCardId,
  )
  if (activeIndex === -1) return null

  const overCardId = parseTaskCardDndId(overDndId)
  const isOverColumnRoot = parseTaskColumnDndId(overDndId) != null

  if (activeColumnId === overColumnId) {
    if (overCardId == null) {
      if (!isOverColumnRoot) return null

      const overIndex = Math.max(overColumn.cards.length - 1, 0)
      if (activeIndex === overIndex) return null

      const nextCards = arrayMove(overColumn.cards, activeIndex, overIndex)

      return columns.map((column) =>
        column.id === activeColumnId
          ? {
              ...column,
              cards: nextCards,
              cardsCount: nextCards.length,
            }
          : column,
      )
    }

    const overIndex = overColumn.cards.findIndex(
      (card) => card.id === overCardId,
    )
    if (overIndex === -1 || activeIndex === overIndex) return null

    const nextCards = arrayMove(overColumn.cards, activeIndex, overIndex)

    return columns.map((column) =>
      column.id === activeColumnId
        ? {
            ...column,
            cards: nextCards,
            cardsCount: nextCards.length,
          }
        : column,
    )
  }

  const card = activeColumn.cards[activeIndex]
  let insertIndex = overColumn.cards.length

  if (overCardId != null) {
    const overIndex = overColumn.cards.findIndex(
      (card) => card.id === overCardId,
    )
    if (overIndex >= 0) {
      const isBelowOverItem =
        activeTop != null &&
        overRect != null &&
        activeTop > overRect.top + overRect.height / 2

      insertIndex = overIndex + (isBelowOverItem ? 1 : 0)
    }
  } else if (isOverColumnRoot) {
    insertIndex = overColumn.cards.length
  }

  const sourceCards = activeColumn.cards.filter(
    (item) => item.id !== activeCardId,
  )
  const targetCards = overColumn.cards.filter(
    (item) => item.id !== activeCardId,
  )
  targetCards.splice(insertIndex, 0, card)

  return columns.map((column) => {
    if (column.id === activeColumnId) {
      return {
        ...column,
        cards: sourceCards,
        cardsCount: sourceCards.length,
      }
    }

    if (column.id === overColumnId) {
      return {
        ...column,
        cards: targetCards,
        cardsCount: targetCards.length,
      }
    }

    return column
  })
}

export const dedupeCardsInColumns = (
  columns: TaskTrackerBoardColumn[],
): TaskTrackerBoardColumn[] => {
  const seenCardIds = new Set<number>()

  return columns.map((column) => {
    const cards = column.cards.filter((card) => {
      if (seenCardIds.has(card.id)) return false
      seenCardIds.add(card.id)
      return true
    })

    if (cards.length === column.cards.length) return column

    return {
      ...column,
      cards,
      cardsCount: cards.length,
    }
  })
}

export const applyMovedTaskCardToColumns = (
  columns: TaskTrackerBoardColumn[],
  updatedCard: TaskTrackerBoardCard,
): TaskTrackerBoardColumn[] => {
  const columnsWithoutCard = columns.map((column) => {
    const cards = column.cards.filter((card) => card.id !== updatedCard.id)

    return {
      ...column,
      cards,
      cardsCount: cards.length,
    }
  })

  return dedupeCardsInColumns(
    columnsWithoutCard.map((column) => {
      if (column.id !== updatedCard.columnId) return column

      const cards = [...column.cards, updatedCard]

      return {
        ...column,
        cards,
        cardsCount: cards.length,
      }
    }),
  )
}

export const replaceCardInColumns = (
  columns: TaskTrackerBoardColumn[],
  updatedCard: TaskTrackerBoardCard,
): TaskTrackerBoardColumn[] =>
  columns.map((column) => {
    const cardIndex = column.cards.findIndex(
      (card) => card.id === updatedCard.id,
    )
    if (cardIndex === -1) return column

    const nextCards = [...column.cards]
    nextCards[cardIndex] = {
      ...nextCards[cardIndex],
      ...updatedCard,
      stickers: mergeTaskStickerValues(
        nextCards[cardIndex].stickers,
        updatedCard.stickers,
      ),
      columnId: column.id,
    }

    return {
      ...column,
      cards: nextCards,
      cardsCount: nextCards.length,
    }
  })

export const applySyncedTaskCardToColumns = (
  columns: TaskTrackerBoardColumn[],
  updatedCard: TaskTrackerBoardCard,
): TaskTrackerBoardColumn[] => {
  const currentColumnId = findColumnIdByCardId(columns, updatedCard.id)

  if (currentColumnId == null || currentColumnId !== updatedCard.columnId) {
    return applyMovedTaskCardToColumns(columns, updatedCard)
  }

  return replaceCardInColumns(columns, updatedCard)
}

export const mergeSyncedTaskCardWithoutColumnMove = (
  columns: TaskTrackerBoardColumn[],
  updatedCard: TaskTrackerBoardCard,
): TaskTrackerBoardColumn[] => {
  const currentColumnId = findColumnIdByCardId(columns, updatedCard.id)

  if (currentColumnId == null) {
    return applySyncedTaskCardToColumns(columns, updatedCard)
  }

  if (currentColumnId === updatedCard.columnId) {
    return replaceCardInColumns(columns, updatedCard)
  }

  const currentColumn = columns.find((column) => column.id === currentColumnId)
  const currentCard = currentColumn?.cards.find(
    (card) => card.id === updatedCard.id,
  )

  return replaceCardInColumns(columns, {
    ...updatedCard,
    columnId: currentColumnId,
    position: currentCard?.position ?? updatedCard.position,
  })
}

export const removeCardFromColumns = (
  columns: TaskTrackerBoardColumn[],
  cardId: number,
): TaskTrackerBoardColumn[] =>
  columns.map((column) => {
    const cards = column.cards.filter((card) => card.id !== cardId)
    if (cards.length === column.cards.length) return column

    return {
      ...column,
      cards,
      cardsCount: cards.length,
    }
  })

export const appendCardsToColumn = (
  columns: TaskTrackerBoardColumn[],
  columnId: number,
  newCards: TaskTrackerBoardCard[],
  totalCount: number,
): TaskTrackerBoardColumn[] =>
  columns.map((column) => {
    if (column.id !== columnId) return column

    const existingIds = new Set(column.cards.map((card) => card.id))
    const cardsToAppend = newCards.filter((card) => !existingIds.has(card.id))
    const cards = [...column.cards, ...cardsToAppend]

    return {
      ...column,
      cards,
      cardsCount: totalCount,
    }
  })

export const replaceColumnCards = (
  columns: TaskTrackerBoardColumn[],
  columnId: number,
  cards: TaskTrackerBoardCard[],
  totalCount: number,
): TaskTrackerBoardColumn[] =>
  columns.map((column) =>
    column.id === columnId
      ? {
          ...column,
          cards,
          cardsCount: totalCount,
        }
      : column,
  )
